/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import * as Sentry from '@sentry/react';
import * as Highcharts from 'highcharts';
import 'highcharts/modules/offline-exporting';
import 'highcharts/modules/export-data';
import HighchartsExporting from 'highcharts/modules/exporting';
import moment from 'moment';
import { dateFormats, sensorUnits, tooltipBorderColor, colors } from 'commons/src/constants';
import generateImageFromSvg from './insightPdfs/GenerateImageFromSvg';

export const generateImage = (
    callback: (response: { data: string; serialNumber: string }) => void,
    setChartError: (error: boolean) => void,
    serialNumber: string
): void => {
    HighchartsExporting(Highcharts);
    const charts = [...Highcharts.charts];
    const reversedCharts = charts.reverse();
    const chart = reversedCharts.find(
        svg =>
            svg &&
            svg.userOptions &&
            svg.userOptions.chart &&
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            svg.userOptions.chart.id === serialNumber
    );

    if (chart === undefined) {
        setChartError(true);
        if (Sentry) {
            Sentry.captureEvent({
                message: 'Generate pdf highcharts chart undefined',
                level: 'warning',
                tags: {
                    errorType: 'generated pdf',
                },
            });
        }
        return;
    }
    const svg = chart.getSVG();
    const width = 1000;
    const height = 250;
    generateImageFromSvg(width, height, svg, callback, serialNumber);
};

const generateConfig = (
    chartData: number[],
    thresholds: { value?: number; color: string }[],
    unit: string,
    chartHeight: number,
    minValue: number,
    dateFormat: keyof typeof dateFormats,
    weekdays: string[],
    plotBands: { color: string; from: number; to: number }[],
    serialNumber: string
) => ({
    credits: {
        enabled: false,
    },
    title: {
        text: '',
    },
    chart: {
        id: serialNumber,
        height: chartHeight,
        type: 'line',
        spacingBottom: 5,
        style: {
            fontFamily: 'OpenSans-Regular',
        },
    },
    tooltip: {
        borderColor: tooltipBorderColor,
        style: {
            color: colors.white,
        },
        hideDelay: 0,
        formatter(this: Highcharts.TooltipFormatterContextObject): string {
            const sensorValue = this.y as number;
            let yValue: string | number = Math.round(sensorValue * 10) / 10;
            const timeValue = this.x as number;
            yValue =
                yValue % 1 === 0 || unit === sensorUnits.bq
                    ? Highcharts.numberFormat(yValue, 0)
                    : Highcharts.numberFormat(yValue, 1);
            const weekDay = (): string => {
                const startOfDay = timeValue - (timeValue % 24);
                const weekDayNumber = startOfDay / 24;
                return weekdays[weekDayNumber];
            };
            const dayTime = (): string => {
                const hourOfDay = timeValue % 24;
                return moment(`${hourOfDay}:00`, 'HH:mm').format(dateFormats[dateFormat].timeOfDay);
            };
            return `${weekDay()} ${dayTime()}<br />${yValue} ${unit}`;
        },
    },
    xAxis: {
        lineColor: 'transparent',
        tickLength: 0,
        plotBands,
        tickInterval: 12,
        type: 'linear',
        gridLineWidth: 1,
        margin: 0,
        labels: {
            formatter(this: { value: number }): string {
                const { value } = this;
                if (value % 24 === 12) {
                    return weekdays[(value - 12) / 24];
                }
                return '';
            },
        },
    },
    yAxis: {
        min: minValue,
        gridLineWidth: 0,
        tickPixelInterval: 40,
        title: {
            text: null,
        },
    },
    series: [
        {
            type: 'line',
            lineWidth: 2.3,
            zones: thresholds,
            showInLegend: false,
            data: chartData,
            connectNulls: false,
            marker: {
                enabled: false,
                symbol: 'circle',
            },
            states: {
                hover: { lineWidthPlus: 0 },
            },
        },
    ],
    plotOptions: {
        series: {
            states: {
                hover: { lineWidthPlus: 0 },
            },
            animation: { duration: 1200 },
        },
    },
    exporting: {
        sourceWidth: 1000,
        sourceHeight: 250,
        chartOptions: {
            subtitle: null,
        },
        buttons: {
            contextButton: {
                enabled: false,
            },
        },
    },
});

export default generateConfig;

export type GenerateConfig = ReturnType<typeof generateConfig>;
